import { useRef } from 'react';
import {
  customerHeaderRefreshApplySchema,
  customerHeaderRefreshPreviewSchema,
  customerHeaderDocumentOverrideUpdateSchema,
  documentDetailSchema,
  documentEditorDefaultsSchema,
  type DocumentEditorDefaults,
  documentCustomerPreviewSchema,
  type DocumentCustomerPreviewInput,
  documentEmailPreviewSchema,
  taxDeterminationSnapshotSchema,
  portalOrderReviewSchema,
  documentsListResponseSchema,
  emailLogSchema,
  type CustomerHeaderRefreshApplyInput,
  type CustomerHeaderRefreshPreview,
  type CustomerHeaderDocumentOverrideUpdateInput,
  type DocumentConvertInput,
  type DocumentCreateInput,
  type DocumentDetail,
  type DocumentEmailPreview,
  type DocumentEmailSendInput,
  type DocumentUpdateInput,
  type DocumentsListResponse,
  type EmailLogEntry,
  type PortalOrderReview,
  type TaxDeterminationPreviewInput,
  type TaxDeterminationSnapshot,
} from '@erp/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiDownload, apiFetch, apiSend, saveBlob, type ApiError } from '@/lib/api-client';

export interface DocumentListParams {
  page: number;
  pageSize: number;
  sort: string;
  q?: string | undefined;
  type?: string[];
  status?: string[];
  paymentStatus?: string[];
  customerId?: string | undefined;
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
  overdueOnly?: boolean | undefined;
  openOnly?: boolean | undefined;
}

export function buildDocumentQuery(params: DocumentListParams): string {
  const search = new URLSearchParams({
    page: String(params.page),
    pageSize: String(params.pageSize),
    sort: params.sort,
  });

  if (params.q !== undefined && params.q.length > 0) {
    search.set('q', params.q);
  }
  if (params.type !== undefined && params.type.length > 0) {
    search.set('type', params.type.join(','));
  }
  if (params.status !== undefined && params.status.length > 0) {
    search.set('status', params.status.join(','));
  }
  if (params.paymentStatus !== undefined && params.paymentStatus.length > 0) {
    search.set('paymentStatus', params.paymentStatus.join(','));
  }
  if (params.customerId !== undefined) {
    search.set('customerId', params.customerId);
  }
  if (params.dateFrom !== undefined) {
    search.set('dateFrom', params.dateFrom);
  }
  if (params.dateTo !== undefined) {
    search.set('dateTo', params.dateTo);
  }
  if (params.overdueOnly === true) {
    search.set('overdueOnly', 'true');
  }
  if (params.openOnly === true) {
    search.set('openOnly', 'true');
  }

  return search.toString();
}

export const documentKeys = {
  all: ['documents'] as const,
  list: (params: DocumentListParams) => ['documents', 'list', params] as const,
  detail: (id: string) => ['documents', 'detail', id] as const,
  emailPreview: (id: string) => ['documents', 'detail', id, 'email-preview'] as const,
  pdf: (id: string, version: number) => ['documents', 'detail', id, 'pdf', version] as const,
  customerHeaderRefresh: (id: string) =>
    ['documents', 'detail', id, 'customer-header-refresh'] as const,
  taxDetermination: (input: TaxDeterminationPreviewInput) =>
    ['documents', 'tax-determination', input] as const,
};

export function useDocumentList(
  params: DocumentListParams,
  enabled = true,
): UseQueryResult<DocumentsListResponse, ApiError> {
  return useQuery({
    enabled,
    queryKey: documentKeys.list(params),
    queryFn: () =>
      apiFetch(`/api/v1/documents?${buildDocumentQuery(params)}`, documentsListResponseSchema),
  });
}

export function useDocument(id: string | undefined): UseQueryResult<DocumentDetail, ApiError> {
  return useQuery({
    queryKey: documentKeys.detail(id ?? ''),
    queryFn: () => apiFetch(`/api/v1/documents/${id ?? ''}`, documentDetailSchema),
    enabled: id !== undefined,
  });
}

export function useDocumentEditorDefaults(
  enabled: boolean,
): UseQueryResult<DocumentEditorDefaults, ApiError> {
  return useQuery({
    queryKey: ['documents', 'defaults'],
    queryFn: () => apiFetch('/api/v1/documents/defaults', documentEditorDefaultsSchema),
    enabled,
    refetchOnMount: 'always',
  });
}

export function useTaxDeterminationPreview(
  input: TaxDeterminationPreviewInput | null,
): UseQueryResult<TaxDeterminationSnapshot, ApiError> {
  return useQuery({
    queryKey:
      input === null
        ? ['documents', 'tax-determination', 'disabled']
        : documentKeys.taxDetermination(input),
    queryFn: () =>
      apiFetch('/api/v1/documents/tax-determination/preview', taxDeterminationSnapshotSchema, {
        method: 'POST',
        body: input ?? {},
      }),
    enabled: input !== null,
    staleTime: 30_000,
  });
}

export function useDocumentCustomerPreview(input: DocumentCustomerPreviewInput | null) {
  return useQuery({
    queryKey: ['documents', 'customer-preview', input],
    queryFn: () =>
      apiFetch('/api/v1/documents/customer-preview', documentCustomerPreviewSchema, {
        method: 'POST',
        body: input,
      }),
    enabled: input !== null,
  });
}

export function useCreateDocument(): UseMutationResult<
  DocumentDetail,
  ApiError,
  DocumentCreateInput
> {
  const queryClient = useQueryClient();
  const idempotencyKey = useRef(crypto.randomUUID());

  return useMutation({
    mutationFn: (input: DocumentCreateInput) =>
      apiFetch('/api/v1/documents', documentDetailSchema, {
        method: 'POST',
        body: input,
        headers: { 'Idempotency-Key': idempotencyKey.current },
      }),
    onSuccess: (created) => {
      queryClient.setQueryData(documentKeys.detail(created.id), created);
      void queryClient.invalidateQueries({ queryKey: documentKeys.all });
    },
  });
}

export function useUpdateDocument(
  id: string,
): UseMutationResult<DocumentDetail, ApiError, DocumentUpdateInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: DocumentUpdateInput) =>
      apiFetch(`/api/v1/documents/${id}`, documentDetailSchema, { method: 'PATCH', body: input }),
    onSuccess: (updated) => {
      queryClient.setQueryData(documentKeys.detail(id), updated);
      void queryClient.invalidateQueries({ queryKey: documentKeys.all });
    },
  });
}

export function useCustomerHeaderRefreshPreview(
  id: string,
): UseMutationResult<CustomerHeaderRefreshPreview, ApiError, void> {
  return useMutation({
    mutationFn: () =>
      apiFetch(
        `/api/v1/documents/${id}/customer-header/refresh-preview`,
        customerHeaderRefreshPreviewSchema,
        { method: 'POST' },
      ),
  });
}

export function useApplyCustomerHeaderRefresh(
  id: string,
): UseMutationResult<DocumentDetail, ApiError, CustomerHeaderRefreshApplyInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiFetch(`/api/v1/documents/${id}/customer-header/refresh`, documentDetailSchema, {
        method: 'POST',
        body: customerHeaderRefreshApplySchema.parse(input),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(documentKeys.detail(id), updated);
      void queryClient.invalidateQueries({ queryKey: documentKeys.all });
    },
  });
}

export function useUpdateDocumentCustomerHeader(
  id: string,
): UseMutationResult<DocumentDetail, ApiError, CustomerHeaderDocumentOverrideUpdateInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiFetch(`/api/v1/documents/${id}/customer-header`, documentDetailSchema, {
        method: 'PUT',
        body: customerHeaderDocumentOverrideUpdateSchema.parse(input),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(documentKeys.detail(id), updated);
      void queryClient.invalidateQueries({ queryKey: documentKeys.all });
    },
  });
}

export function useDeleteDocument(): UseMutationResult<void, ApiError, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiSend(`/api/v1/documents/${id}`, { method: 'DELETE' }),
    onSuccess: async (_result, id) => {
      // The editor stays mounted until navigation; refetching its deleted record would produce a 404.
      await queryClient.cancelQueries({ queryKey: documentKeys.detail(id) });
      void queryClient.invalidateQueries({
        queryKey: documentKeys.detail(id),
        refetchType: 'none',
      });
      void queryClient.invalidateQueries({
        queryKey: documentKeys.all,
        predicate: (query) => !(query.queryKey[1] === 'detail' && query.queryKey[2] === id),
      });
    },
  });
}

export function useConvertDocument(
  id: string,
): UseMutationResult<DocumentDetail, ApiError, DocumentConvertInput> {
  const queryClient = useQueryClient();
  const retry = useRef<{ body: string; key: string } | null>(null);

  return useMutation({
    mutationFn: async (input: DocumentConvertInput) => {
      const storageKey = `erp:conversion:${id}:${JSON.stringify(input)}`;
      const idempotencyKey = sessionStorage.getItem(storageKey) ?? crypto.randomUUID();
      sessionStorage.setItem(storageKey, idempotencyKey);
      const result = await apiFetch(`/api/v1/documents/${id}/convert`, documentDetailSchema, {
        method: 'POST',
        body: input,
        headers: { 'Idempotency-Key': idempotencyKey },
      });
      sessionStorage.removeItem(storageKey);
      return result;
    },
    onSuccess: (created) => {
      retry.current = null;
      queryClient.setQueryData(documentKeys.detail(created.id), created);
      void queryClient.invalidateQueries({ queryKey: documentKeys.all });
    },
  });
}

export function useLockDocument(
  id: string,
): UseMutationResult<
  DocumentDetail,
  ApiError,
  { version: number; advancePreviewHash?: string; idempotencyKey?: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      idempotencyKey,
      ...input
    }: {
      version: number;
      advancePreviewHash?: string;
      idempotencyKey?: string;
    }) =>
      apiFetch(`/api/v1/documents/${id}/lock`, documentDetailSchema, {
        method: 'POST',
        body: input,
        ...(idempotencyKey ? { headers: { 'idempotency-key': idempotencyKey } } : {}),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(documentKeys.detail(id), updated);
      void queryClient.invalidateQueries({ queryKey: documentKeys.all });
    },
  });
}

export function useUpdateDocumentStatus(
  id: string,
): UseMutationResult<DocumentDetail, ApiError, { status: string; version: number }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { status: string; version: number }) =>
      apiFetch(`/api/v1/documents/${id}/status`, documentDetailSchema, {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(documentKeys.detail(id), updated);
      void queryClient.invalidateQueries({ queryKey: documentKeys.all });
    },
  });
}

export function useReviewPortalOrder(
  id: string,
): UseMutationResult<DocumentDetail, ApiError, PortalOrderReview> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      apiFetch(`/api/v1/documents/${id}/portal-review`, documentDetailSchema, {
        method: 'POST',
        body: portalOrderReviewSchema.parse(input),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(documentKeys.detail(id), updated);
      void queryClient.invalidateQueries({ queryKey: documentKeys.all });
    },
  });
}

export function useEmailPreview(
  id: string,
  enabled: boolean,
): UseQueryResult<DocumentEmailPreview, ApiError> {
  return useQuery({
    queryKey: documentKeys.emailPreview(id),
    queryFn: () => apiFetch(`/api/v1/documents/${id}/email-preview`, documentEmailPreviewSchema),
    enabled,
    staleTime: 0,
  });
}

export function useSendDocumentEmail(
  id: string,
): UseMutationResult<EmailLogEntry, ApiError, DocumentEmailSendInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: DocumentEmailSendInput) =>
      apiFetch(`/api/v1/documents/${id}/send-email`, emailLogSchema, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: documentKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: documentKeys.all });
    },
  });
}

/** Lädt das serverseitig gerenderte PDF als Objekt-URL für die Vorschau. */
export function useDocumentPdf(
  id: string | undefined,
  version: number | undefined,
): UseQueryResult<Blob, ApiError> {
  return useQuery({
    queryKey: documentKeys.pdf(id ?? '', version ?? 0),
    queryFn: async () => {
      const result = await apiDownload(`/api/v1/documents/${id ?? ''}/pdf`, 'beleg.pdf');
      return result.blob;
    },
    enabled: id !== undefined && version !== undefined,
    staleTime: 0,
    gcTime: 60_000,
  });
}

export function useDocumentPdfDownload(): UseMutationResult<
  void,
  ApiError,
  { id: string; filename: string }
> {
  return useMutation({
    mutationFn: async ({ id, filename }) => {
      saveBlob(await apiDownload(`/api/v1/documents/${id}/pdf`, filename));
    },
  });
}
